// src/screens/DiscoveryView.jsx

import React, { useState, useEffect, useCallback, useRef } from "react";
import Icon from "../../../components/Icon";
import Tooltip from "../../../components/Tooltip";
import Modal from "../../../components/Modal";
import SearchInput from "../../../components/SearchInput";
import ConfirmModal from "../../../components/ConfirmModal";
import { EmptyState } from "../../../components/State";
import {
  fetchSubnets,
  fetchAssets,
  testConnection,
  runDiscoveryScan,
  runAgentDeploy,
  getJobProgress,
  deleteSubnet,
  fetchMeta,
} from "../services/DiscoveryService";
import { getStoredAuth } from "../../../api";
import { getAssetCategory, getAssetManagementType } from "../../../utils/assetTaxonomy";
import SubnetRelayWizardModal from "../components/discovery/SubnetRelayWizardModal";
import DeployToMonitoringModal from "../components/discovery/DeployToMonitoringModal";
import SubnetScanOverlayModal from "../components/discovery/SubnetScanOverlayModal";
import SubnetProfileDrawer from "../components/discovery/SubnetProfileDrawer";
import AssetProfileDrawer from "../components/discovery/AssetProfileDrawer";

export default function DiscoveryView() {
  const getAuthorizedByName = () => {
    try {
      const auth = getStoredAuth();
      if (auth) {
        if (auth.user) {
          if (auth.user.name) return auth.user.name;
          if (auth.user.email) return auth.user.email;
          if (auth.user.username) return auth.user.username;
        }
        if (auth.name) return auth.name;
        if (auth.email) return auth.email;
        if (auth.username) return auth.username;

        const token = localStorage.getItem("aiops_token");
        if (token) {
          const parts = token.split(".");
          if (parts.length === 3) {
            const payload = JSON.parse(
              window.atob(parts[1].replace(/-/g, "+").replace(/_/g, "/")),
            );
            if (payload) {
              if (payload.name) return payload.name;
              if (payload.email) return payload.email;
              if (payload.username) return payload.username;
              if (payload.sub) return payload.sub;
            }
          }
        }
      }
    } catch (e) {
      console.error("Error extracting user name", e);
    }
    return "you@company.com";
  };

  // Navigation & Tab states
  const [activeTab, setActiveTab] = useState("subnets"); // 'subnets' or 'assets'
  const [activeJob, setActiveJob] = useState(null); // Selected deployment job for terminal display

  // Data states
  const [subnets, setSubnets] = useState([]);
  const [assets, setAssets] = useState([]);
  const [jobsHistory, setJobsHistory] = useState([]);

  // Loading & Error states
  const [loading, setLoading] = useState(true);
  // eslint-disable-next-line no-unused-vars
  const [error, setError] = useState(null);

  // Filter states
  const [searchQuery, setSearchQuery] = useState("");
  const [subnetFilter, setSubnetFilter] = useState("");
  const [typeFilter, setTypeFilter] = useState("");
  const [statusFilter, setStatusFilter] = useState("");

  // Checkbox selection state
  const [selectedAssetIds, setSelectedAssetIds] = useState(new Set());
  const [isRedeployMode, setIsRedeployMode] = useState(false);

  // Slide-over Drawers state
  const [selectedSubnetDrawer, setSelectedSubnetDrawer] = useState(null);
  const [selectedAssetDrawer, setSelectedAssetDrawer] = useState(null);

  // Add Subnet Modal state
  const [showAddSubnetModal, setShowAddSubnetModal] = useState(false);
  const [subnetToDelete, setSubnetToDelete] = useState(null);
  const [deleteSubnetError, setDeleteSubnetError] = useState(null);
  const [assetsPage, setAssetsPage] = useState(1);
  const [operationError, setOperationError] = useState(null);

  // Deploy Confirmation Modal state
  const [showDeployModal, setShowDeployModal] = useState(false);
  const [deployConcurrency, setDeployConcurrency] = useState(5);
  const [deployFields, setDeployFields] = useState({}); // mapping assetId -> { ssh_user, kafka_broker }

  // Connection Testing state
  const [testingConnectionIp, setTestingConnectionIp] = useState(null);
  const [connectionTestResult, setConnectionTestResult] = useState({}); // mapping assetIp -> { success, details }

  // Discovery Scan overlay state
  const [activeScanSubnet, setActiveScanSubnet] = useState(null);
  const [scanTerminalLines, setScanTerminalLines] = useState([]);
  const [scanProgress, setScanProgress] = useState(0);
  const [scanFinished, setScanFinished] = useState(false);
  const [scanResultSummary, setScanResultSummary] = useState("");

  // Job progress simulator state
  const [jobTerminalLines, setJobTerminalLines] = useState([]);
  const [isJobSimulating, setIsJobSimulating] = useState(false);

  const [availableTypes, setAvailableTypes] = useState([
    "server",
    "db",
    "router",
    "switch",
    "firewall",
    "load_balancer",
    "gateway",
    "container",
    "storage",
    "unclassified",
  ]);
  const [availableStatuses, setAvailableStatuses] = useState([
    "discovered",
    "onboarded",
    "stale",
  ]);

  // Ref pointers to keep terminals scrolled to bottom
  const terminalBottomRef = useRef(null);
  const scanTerminalBottomRef = useRef(null);

  // Fetch subnets, assets & metadata
  const loadData = useCallback(async (isBackground = false) => {
    if (!isBackground) setLoading(true);
    setError(null);
    try {
      const [subnetsData, assetsData, metaData] = await Promise.all([
        fetchSubnets(),
        fetchAssets(),
        fetchMeta().catch((err) => {
          console.error("Failed to load discovery meta, using defaults:", err);
          return null;
        }),
      ]);
      setSubnets(subnetsData || []);
      setAssets(assetsData || []);
      if (metaData) {
        if (Array.isArray(metaData.types)) {
          const filteredTypes = metaData.types.filter(
            (t) => t !== "net" && t !== "mem",
          );
          const defaultNetworkTypes = [
            "server",
            "db",
            "router",
            "switch",
            "firewall",
            "load_balancer",
            "gateway",
            "container",
            "storage",
            "unclassified",
          ];
          const mergedTypes = Array.from(
            new Set([...filteredTypes, ...defaultNetworkTypes]),
          );
          setAvailableTypes(mergedTypes);
        }
        if (Array.isArray(metaData.statuses))
          setAvailableStatuses(metaData.statuses);
      }
    } catch (e) {
      setError("Failed to fetch discovery and asset data.");
      console.error(e);
    } finally {
      if (!isBackground) setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadData(false);
    const interval = setInterval(() => {
      loadData(true);
    }, 10000);
    return () => clearInterval(interval);
  }, [loadData]);

  // Automatically refresh assets when switching to the assets tab
  useEffect(() => {
    if (activeTab === "assets") {
      loadData(true);
    }
  }, [activeTab, loadData]);

  // Scroll to bottoms of terminals
  useEffect(() => {
    if (terminalBottomRef.current) {
      terminalBottomRef.current.scrollIntoView({ behavior: "smooth" });
    }
  }, [jobTerminalLines]);

  useEffect(() => {
    if (scanTerminalBottomRef.current) {
      scanTerminalBottomRef.current.scrollIntoView({ behavior: "smooth" });
    }
  }, [scanTerminalLines]);

  useEffect(() => {
    setAssetsPage(1);
  }, [searchQuery, subnetFilter, typeFilter, statusFilter]);

  const handleConfirmDeleteSubnet = async () => {
    if (!subnetToDelete) return;
    setDeleteSubnetError(null);
    try {
      await deleteSubnet(subnetToDelete.id);
      setSubnetToDelete(null);
      loadData();
    } catch (err) {
      setDeleteSubnetError(err.message || "Failed to delete subnet.");
    }
  };

  // Subnet Scan Sweeper Simulation (using ScanStream)
  const handleRunSubnetScan = (subnet) => {
    setActiveScanSubnet(subnet);
    setScanProgress(0);
    setScanFinished(false);
    setScanResultSummary("");
    setScanTerminalLines([]);

    const nowStr = new Date().toLocaleString();
    setSubnets((prevSubnets) =>
      prevSubnets.map((s) =>
        s.id === subnet.id ? { ...s, lastScan: nowStr } : s,
      ),
    );
    if (selectedSubnetDrawer && selectedSubnetDrawer.id === subnet.id) {
      setSelectedSubnetDrawer((prev) => ({ ...prev, lastScan: nowStr }));
    }

    const stream = runDiscoveryScan(subnet.id);

    stream.on("log", (line) => {
      let type = "info";
      if (line.includes("Starting") || line.includes("Scanning")) type = "info";
      else if (line.includes("SUCCESS")) type = "success";
      else if (line.includes("NEW")) type = "new";
      else if (line.includes("resolving") || line.includes("sweep"))
        type = "dim";

      // Parse percentage from nmap stats output: e.g. "About 33.50% done"
      const match = line.match(/About\s+([0-9.]+)%\s+done/i);
      if (match) {
        setScanProgress(parseFloat(match[1]));
      }

      setScanTerminalLines((prev) => [...prev, { t: line, type }]);
    });

    stream.on("status", (status) => {
      if (status === "completed") {
        setScanProgress(100);
        setScanFinished(true);
        setScanResultSummary(
          "✓ Subnet discovery completed. Asset lists refreshed.",
        );
        loadData(true);
        // Poll for updates as the ingestion pipeline catches up asynchronously
        let count = 0;
        const intervalId = setInterval(() => {
          loadData(true);
          count++;
          if (count >= 5) clearInterval(intervalId);
        }, 2000);
      } else if (status === "failed") {
        setScanFinished(true);
        setScanResultSummary(
          "❌ Subnet discovery failed. Please review the logs above.",
        );
        loadData(true);
      }
    });

    stream.on("complete", () => {
      setScanFinished(true);
      loadData();
    });

    stream.on("error", (err) => {
      setScanTerminalLines((prev) => [
        ...prev,
        { t: `[ERROR] ${err}`, type: "error" },
      ]);
      setScanFinished(true);
    });
  };

  // Connection testing handler
  const handleTestConnection = async (asset, e) => {
    e.stopPropagation();
    setTestingConnectionIp(asset.ip);
    try {
      const res = await testConnection(asset.id);
      setConnectionTestResult((prev) => ({
        ...prev,
        [asset.ip]: { success: res.success, details: res.details },
      }));
      loadData(); // reload to show updated relative times
    } catch (err) {
      setConnectionTestResult((prev) => ({
        ...prev,
        [asset.ip]: { success: false, details: err.message || "Failed check." },
      }));
    } finally {
      setTestingConnectionIp(null);
    }
  };

  // Checkbox multi-select helpers
  const handleToggleSelectAsset = (assetId) => {
    setSelectedAssetIds((prev) => {
      const next = new Set(prev);
      if (next.has(assetId)) {
        next.delete(assetId);
      } else {
        next.add(assetId);
      }
      return next;
    });
  };

  const handleToggleSelectAll = (e) => {
    if (e.target.checked) {
      const filterable = getFilteredAssets().filter(
        (a) => (a.management_type || a.managementType) !== "agentless_snmp",
      );
      setSelectedAssetIds(new Set(filterable.map((a) => a.id)));
    } else {
      setSelectedAssetIds(new Set());
    }
  };

  // Open deployment batch modal
  const handleOpenDeployModal = (assetIds, options = {}) => {
    const fields = {};
    assetIds.forEach((id) => {
      fields[id] = {
        ssh_user: "ubuntu",
        kafka_broker:
          process.env.REACT_APP_KAFKA_BROKER || "10.128.15.205:9092",
      };
    });
    setDeployFields(fields);

    // Auto-detect redeploy mode if all target assets are already onboarded, or if passed in options
    const targetAssets = assets.filter((a) => assetIds.includes(a.id));
    const shouldRedeploy =
      options.isRedeploy !== undefined
        ? options.isRedeploy
        : targetAssets.length > 0 && targetAssets.every((a) => a.status === "onboarded");
    setIsRedeployMode(shouldRedeploy);
    setShowDeployModal(true);
  };

  // Deploy trigger
  const handleStartDeploy = async () => {
    setShowDeployModal(false);
    const hostIds = Object.keys(deployFields);
    const targetAssets = assets.filter((a) => hostIds.includes(a.id));
    const ips = targetAssets.map((a) => a.ip);

    setSelectedAssetIds(new Set()); // Reset checkboxes

    try {
      const res = await runAgentDeploy(ips, getAuthorizedByName(), isRedeployMode);
      if (res.success && res.jobId) {
        const newJob = {
          id: res.jobId,
          status: "running",
          created_at: new Date().toISOString(),
          created_by: getAuthorizedByName(),
          concurrency: deployConcurrency,
          hosts: targetAssets.map((a) => ({
            id: a.id,
            name: a.name,
            ip: a.ip,
            status: "queued",
          })),
        };

        // Save to past jobs list
        setJobsHistory((prev) => [newJob, ...prev]);

        // Start Streaming CLI Logs
        startJobProgressStream(newJob);
      }
    } catch (e) {
      console.error(e);
      setOperationError("Failed to start agent deployment.");
    }
  };

  // Subscribe to Job progress stream
  const startJobProgressStream = (job) => {
    setActiveJob(job);
    setJobTerminalLines([]);
    setIsJobSimulating(true);

    const stream = getJobProgress(job.id);

    stream.on("log", (logObj) => {
      if (!logObj) return;
      let line;
      let isError = false;
      if (typeof logObj === "string") {
        line = logObj;
        isError =
          logObj.toLowerCase().includes("error") ||
          logObj.toLowerCase().includes("fail");
      } else {
        // logObj shape: { timestamp, ip, type, text }
        line = `[${logObj.timestamp || new Date().toISOString()}] [${logObj.ip || "target"}] ${logObj.text || ""}`;
        isError = logObj.type === "error" || logObj.type === "stderr";
      }
      setJobTerminalLines((prev) => [...prev, { t: line, isError }]);
    });

    stream.on("progress", ({ progress, completed }) => {
      // completed shape: { [ip]: 'success' | 'failed' }
      setActiveJob((prev) => {
        if (!prev) return prev;
        const hostsUpdated = prev.hosts.map((h) => ({
          ...h,
          status: (completed && completed[h.ip]) || h.status,
        }));
        return {
          ...prev,
          hosts: hostsUpdated,
          progress,
        };
      });
    });

    stream.on("status", (status) => {
      if (status === "completed" || status === "failed") {
        setIsJobSimulating(false);
        setActiveJob((prev) => {
          if (!prev) return prev;
          const hasFailures = prev.hosts.some((h) => h.status === "failed");
          return {
            ...prev,
            status: hasFailures ? "completed_with_errors" : "completed",
          };
        });
        loadData(true);
        // Poll for updates as the background deployment completes on the server
        let count = 0;
        const intervalId = setInterval(() => {
          loadData(true);
          count++;
          if (count >= 8) clearInterval(intervalId);
        }, 3000);
      }
    });

    stream.on("complete", () => {
      setIsJobSimulating(false);
      loadData(true);
      // Poll for updates as the background deployment completes on the server
      let count = 0;
      const intervalId = setInterval(() => {
        loadData(true);
        count++;
        if (count >= 8) clearInterval(intervalId);
      }, 3000);
    });

    stream.on("error", (err) => {
      setJobTerminalLines((prev) => [
        ...prev,
        { t: `[ERROR] ${err}`, isError: true },
      ]);
      setIsJobSimulating(false);
    });
  };

  // Retry Failed agent pushes
  const handleRetryFailed = async () => {
    if (!activeJob) return;
    const failedHosts = activeJob.hosts.filter((h) => h.status === "failed");
    const failedIps = failedHosts.map((h) => h.ip);

    if (failedIps.length === 0) return;

    try {
      const res = await runAgentDeploy(failedIps, getAuthorizedByName());
      if (res.success && res.jobId) {
        const retriedJob = {
          id: res.jobId,
          status: "running",
          created_at: new Date().toISOString(),
          created_by: getAuthorizedByName(),
          concurrency: activeJob.concurrency,
          hosts: failedHosts.map((h) => ({ ...h, status: "queued" })),
        };
        startJobProgressStream(retriedJob);
      }
    } catch (e) {
      console.error(e);
      setOperationError("Failed to retry job.");
    }
  };

  // Filters logic
  const getFilteredAssets = () => {
    return assets.filter((asset) => {
      // Subnet filter
      if (subnetFilter && asset.subnet_id !== subnetFilter) return false;
      // Type filter
      if (typeFilter && asset.type !== typeFilter) return false;
      // Status filter
      if (statusFilter) {
        const s = statusFilter.toLowerCase();
        if (s === "onboarded" && asset.status !== "onboarded") return false;
        if (s === "discovered" && asset.status !== "discovered") return false;
        if (s === "stale" && asset.status !== "stale") return false;
      }
      // Text query search
      if (searchQuery) {
        const q = searchQuery.toLowerCase();
        const nameMatch = asset.name?.toLowerCase().includes(q);
        const ipMatch = asset.ip?.includes(q);
        const typeMatch = asset.type?.toLowerCase().includes(q);
        if (!nameMatch && !ipMatch && !typeMatch) return false;
      }
      return true;
    });
  };

  // Sort subnets (recency of scan)
  const getSortedSubnets = () => {
    return [...subnets].sort((a, b) => {
      if (!a.lastScan) return 1;
      if (!b.lastScan) return -1;
      return new Date(b.lastScan) - new Date(a.lastScan);
    });
  };

  return (
    <div
      style={{
        flex: 1,
        display: "flex",
        flexDirection: "column",
        overflow: "hidden",
        background: "#F8FAFC",
        height: "100vh",
      }}
    >
      {/* Top Banner indicating a running job */}
      {isJobSimulating && (
        <div
          onClick={() => setActiveJob(activeJob)}
          style={{
            background: "#EFF6FF",
            borderBottom: "1px solid #BFDBFE",
            padding: "8px 24px",
            fontSize: 13,
            fontWeight: 600,
            color: "#1D4ED8",
            display: "flex",
            alignItems: "center",
            gap: 10,
            cursor: "pointer",
          }}
        >
          <div
            style={{
              width: 12,
              height: 12,
              borderRadius: "50%",
              border: "2px solid rgba(29,78,216,0.2)",
              borderTopColor: "#1D4ED8",
              animation: "spin 0.8s linear infinite",
            }}
          />
          <span>
            Agent deployment job is running. Click here to open the CLI
            Terminal.
          </span>
        </div>
      )}

      {/* HEADER PANEL */}
      <div
        style={{
          padding: "20px 24px 16px",
          borderBottom: "1px solid #E5E7EB",
          background: "#fff",
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          flexShrink: 0,
        }}
      >
        <div>
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <h1
              style={{
                margin: 0,
                fontSize: 18,
                fontWeight: 700,
                color: "#111827",
              }}
            >
              Discovery Console
            </h1>
          </div>
          <p style={{ margin: "4px 0 0", fontSize: 12, color: "#6B7280" }}>
            Audit monitored network subnets, discover hosts, and push AIOps
            collection agents.
          </p>
        </div>

        {/* Jobs List history popover shortcut */}
        {jobsHistory.length > 0 && (
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <span style={{ fontSize: 12, color: "#6B7280" }}>Jobs Logs:</span>
            <select
              onChange={(e) => {
                const job = jobsHistory.find((j) => j.id === e.target.value);
                if (job) {
                  setActiveJob(job);
                  // Load saved terminal logs
                  setJobTerminalLines([]);
                  setIsJobSimulating(false);
                }
              }}
              value=""
              style={{
                padding: "6px 12px",
                border: "1px solid #D1D5DB",
                borderRadius: 6,
                fontSize: 12,
                background: "#fff",
                color: "#374151",
                cursor: "pointer",
              }}
            >
              <option value="" disabled hidden>
                View Job History Logs...
              </option>
              {jobsHistory.map((j) => (
                <option key={j.id} value={j.id}>
                  Job ID: {j.id.slice(0, 8)} (
                  {new Date(j.created_at).toLocaleTimeString()})
                </option>
              ))}
            </select>
          </div>
        )}
      </div>

      {/* IF JOB PROGRESS TERMINAL VIEW IS ACTIVE */}
      {activeJob ? (
        <div
          style={{
            flex: 1,
            display: "flex",
            flexDirection: "column",
            overflow: "hidden",
          }}
        >
          {/* Progress Header */}
          <div
            style={{
              padding: "12px 24px",
              borderBottom: "1px solid #E5E7EB",
              background: "#fff",
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
              <button
                onClick={() => setActiveJob(null)}
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 6,
                  background: "none",
                  border: "none",
                  color: "#1D4ED8",
                  cursor: "pointer",
                  fontSize: 13,
                  fontWeight: 600,
                }}
              >
                <Icon name="chevronLeft" size={14} color="#1D4ED8" /> Return to
                Discovery console
              </button>
              <div style={{ height: 16, width: 1, background: "#E5E7EB" }} />
              <div>
                <span
                  style={{ fontSize: 13, fontWeight: 700, color: "#111827" }}
                >
                  Deployment Job Progress Log —{" "}
                  <span style={{ fontFamily: "monospace", fontSize: 12 }}>
                    {activeJob.id}
                  </span>
                </span>
                <span
                  style={{
                    marginLeft: 8,
                    fontSize: 11,
                    background:
                      activeJob.status === "running"
                        ? "#EFF6FF"
                        : activeJob.status === "completed"
                          ? "#F0FDF4"
                          : "#FEF2F2",
                    color:
                      activeJob.status === "running"
                        ? "#1D4ED8"
                        : activeJob.status === "completed"
                          ? "#15803D"
                          : "#DC2626",
                    padding: "2px 8px",
                    borderRadius: 12,
                    fontWeight: 600,
                  }}
                >
                  {activeJob.status}
                </span>
              </div>
            </div>

            {/* Retry Failed button */}
            {activeJob.status !== "running" &&
              activeJob.hosts.some((h) => h.status === "failed") && (
                <button
                  onClick={handleRetryFailed}
                  style={{
                    display: "inline-flex",
                    alignItems: "center",
                    gap: 6,
                    background: "#1D4ED8",
                    color: "#fff",
                    border: "none",
                    borderRadius: 6,
                    padding: "6px 12px",
                    fontSize: 12.5,
                    fontWeight: 600,
                    cursor: "pointer",
                  }}
                >
                  <Icon name="refresh" size={14} color="#fff" /> Retry Failed
                </button>
              )}
          </div>

          {/* Job Details Meta */}
          <div
            style={{
              padding: "12px 24px",
              background: "#F8FAFC",
              borderBottom: "1px solid #E5E7EB",
              display: "grid",
              gridTemplateColumns: "repeat(auto-fit, minmax(140px, 1fr))",
              gap: 16,
              fontSize: 12,
            }}
          >
            <div>
              <span style={{ color: "#6B7280", display: "block" }}>
                Job ID:
              </span>
              <span
                style={{
                  fontWeight: 600,
                  color: "#374151",
                  fontFamily: "monospace",
                }}
              >
                {activeJob.id}
              </span>
            </div>
            <div>
              <span style={{ color: "#6B7280", display: "block" }}>
                Timestamp:
              </span>
              <span style={{ fontWeight: 600, color: "#374151" }}>
                {new Date(activeJob.created_at).toLocaleString()}
              </span>
            </div>
            <div>
              <span style={{ color: "#6B7280", display: "block" }}>
                Author / Operator:
              </span>
              <span style={{ fontWeight: 600, color: "#374151" }}>
                {activeJob.created_by}
              </span>
            </div>
            <div>
              <span style={{ color: "#6B7280", display: "block" }}>
                Concurrency limit:
              </span>
              <span style={{ fontWeight: 600, color: "#374151" }}>
                {activeJob.concurrency} hosts
              </span>
            </div>
          </div>

          {/* Split CLI Progress Shell */}
          <div style={{ flex: 1, display: "flex", overflow: "hidden" }}>
            {/* Host targets listing */}
            <div
              style={{
                width: 260,
                borderRight: "1px solid #E5E7EB",
                background: "#fff",
                padding: 16,
                overflowY: "auto",
              }}
            >
              <h3
                style={{
                  margin: "0 0 12px",
                  fontSize: 13,
                  fontWeight: 700,
                  color: "#374151",
                }}
              >
                Target Hosts ({activeJob.hosts.length})
              </h3>
              <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                {activeJob.hosts.map((h) => (
                  <div
                    key={h.id}
                    style={{
                      padding: 10,
                      border: "1px solid #E5E7EB",
                      borderRadius: 8,
                      background: "#F8FAFC",
                      display: "flex",
                      flexDirection: "column",
                      gap: 4,
                    }}
                  >
                    <div
                      style={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                      }}
                    >
                      <span
                        style={{
                          fontSize: 12,
                          fontWeight: 700,
                          color: "#111827",
                        }}
                      >
                        {h.name}
                      </span>
                      <span
                        style={{
                          fontSize: 10.5,
                          padding: "2px 6px",
                          borderRadius: 4,
                          fontWeight: 600,
                          background:
                            h.status === "success"
                              ? "#D1FAE5"
                              : h.status === "failed"
                                ? "#FEE2E2"
                                : h.status === "installing"
                                  ? "#DBEAFE"
                                  : "#F3F4F6",
                          color:
                            h.status === "success"
                              ? "#065F46"
                              : h.status === "failed"
                                ? "#981B1B"
                                : h.status === "installing"
                                  ? "#1E40AF"
                                  : "#4B5563",
                          textTransform: "capitalize",
                        }}
                      >
                        {h.status}
                      </span>
                    </div>
                    <span
                      style={{
                        fontSize: 11,
                        fontFamily: "monospace",
                        color: "#6B7280",
                      }}
                    >
                      {h.ip}
                    </span>
                  </div>
                ))}
              </div>
            </div>

            {/* CLI Logs Area */}
            <div
              style={{
                flex: 1,
                background: "#0F172A",
                padding: 16,
                display: "flex",
                flexDirection: "column",
                overflow: "hidden",
              }}
            >
              {/* Window Header Buttons */}
              <div
                style={{
                  height: 36,
                  background: "#1E293B",
                  borderTopLeftRadius: 8,
                  borderTopRightRadius: 8,
                  display: "flex",
                  alignItems: "center",
                  padding: "0 16px",
                  position: "relative",
                  flexShrink: 0,
                }}
              >
                <div style={{ display: "flex", gap: 8 }}>
                  <div
                    style={{
                      width: 12,
                      height: 12,
                      borderRadius: "50%",
                      background: "#EF4444",
                    }}
                  />
                  <div
                    style={{
                      width: 12,
                      height: 12,
                      borderRadius: "50%",
                      background: "#F59E0B",
                    }}
                  />
                  <div
                    style={{
                      width: 12,
                      height: 12,
                      borderRadius: "50%",
                      background: "#10B981",
                    }}
                  />
                </div>
                <div
                  style={{
                    position: "absolute",
                    left: 0,
                    right: 0,
                    textAlign: "center",
                    color: "#94A3B8",
                    fontSize: 11.5,
                    fontFamily: "monospace",
                  }}
                >
                  agent-deploy-shell - {activeJob.id}
                </div>
              </div>

              {/* Logs Screen */}
              <div
                style={{
                  flex: 1,
                  background: "#0B0F19",
                  borderBottomLeftRadius: 8,
                  borderBottomRightRadius: 8,
                  padding: 16,
                  overflowY: "auto",
                  fontFamily: "monospace",
                  fontSize: 12.5,
                  color: "#34D399",
                  lineHeight: 1.6,
                }}
              >
                {jobTerminalLines.length === 0 ? (
                  <div style={{ color: "#475569" }}>
                    {isJobSimulating
                      ? "[awaiting streaming CLI logs...]"
                      : "Logs completed."}
                  </div>
                ) : (
                  jobTerminalLines.map((line, i) => {
                    let color = "#E2E8F0";
                    const text = line?.t || "";
                    if (
                      line?.isError ||
                      text.includes("failed") ||
                      text.includes("refused")
                    ) {
                      color = "#F87171";
                    } else if (
                      text.includes("successfully") ||
                      text.includes("established")
                    ) {
                      color = "#34D399";
                    } else if (
                      text.includes("Connecting") ||
                      text.includes("Copying")
                    ) {
                      color = "#94A3B8";
                    }
                    return (
                      <div key={i} style={{ color, marginBottom: 2 }}>
                        {text}
                      </div>
                    );
                  })
                )}
                <div ref={terminalBottomRef} />
              </div>
            </div>
          </div>
        </div>
      ) : (
        /* NORMAL SCREEN (SUBNETS AND ASSETS TABS) */
        <div
          style={{
            flex: 1,
            display: "flex",
            flexDirection: "column",
            overflow: "hidden",
            background: "#F8FAFC",
          }}
        >
          {/* Tab bar toolbar with Tabs and active tab filters */}
          {subnets.length > 0 && (
            <div
              style={{
                borderBottom: "1px solid #E5E7EB",
                background: "#fff",
                display: "flex",
                flexDirection: "column",
                flexShrink: 0,
              }}
            >
              {/* Tabs selector row */}
              <div
                style={{
                  padding: "0 24px",
                  borderBottom: "1px solid #F3F4F6",
                  display: "flex",
                  alignItems: "stretch",
                  height: 42,
                }}
              >
                <div style={{ display: "flex", height: "100%" }}>
                  <button
                    onClick={() => setActiveTab("subnets")}
                    style={{
                      display: "inline-flex",
                      alignItems: "center",
                      gap: 6,
                      padding: "0 14px",
                      border: "none",
                      background: "none",
                      cursor: "pointer",
                      fontSize: 12,
                      fontWeight: activeTab === "subnets" ? 700 : 400,
                      color: activeTab === "subnets" ? "#1D4ED8" : "#6B7280",
                      borderBottom:
                        activeTab === "subnets"
                          ? "2px solid #1D4ED8"
                          : "2px solid transparent",
                    }}
                  >
                    <Icon
                      name="globe"
                      size={14}
                      color={activeTab === "subnets" ? "#1D4ED8" : "#9CA3AF"}
                    />
                    Monitored Subnets
                  </button>
                  <button
                    onClick={() => setActiveTab("assets")}
                    style={{
                      display: "inline-flex",
                      alignItems: "center",
                      gap: 6,
                      padding: "0 14px",
                      border: "none",
                      background: "none",
                      cursor: "pointer",
                      fontSize: 12,
                      fontWeight: activeTab === "assets" ? 700 : 400,
                      color: activeTab === "assets" ? "#1D4ED8" : "#6B7280",
                      borderBottom:
                        activeTab === "assets"
                          ? "2px solid #1D4ED8"
                          : "2px solid transparent",
                    }}
                  >
                    <Icon
                      name="server"
                      size={14}
                      color={activeTab === "assets" ? "#1D4ED8" : "#9CA3AF"}
                    />
                    Discovered Assets
                    <span
                      style={{
                        background:
                          activeTab === "assets" ? "#EFF6FF" : "#F3F4F6",
                        color: activeTab === "assets" ? "#1D4ED8" : "#6B7280",
                        padding: "1px 6px",
                        borderRadius: 10,
                        fontSize: 10,
                        fontWeight: 600,
                        fontFamily: "monospace",
                      }}
                    >
                      {getFilteredAssets().length}
                    </span>
                  </button>
                </div>
              </div>

              {/* Filters Row (Asset Tab) */}
              {!loading && activeTab === "assets" && (
                <div
                  style={{
                    padding: "12px 24px",
                    display: "flex",
                    gap: 8,
                    flexWrap: "wrap",
                    alignItems: "center",
                  }}
                >
                  <SearchInput
                    placeholder="Search name, IP, OS guess..."
                    value={searchQuery}
                    onChange={setSearchQuery}
                    style={{ flex: 1, minWidth: 200 }}
                  />

                  <select
                    value={subnetFilter}
                    onChange={(e) => setSubnetFilter(e.target.value)}
                    style={{
                      padding: "7px 10px",
                      border: "1px solid #E5E7EB",
                      borderRadius: 7,
                      fontSize: 12,
                      background: "#fff",
                      color: "#374151",
                      cursor: "pointer",
                    }}
                  >
                    <option value="">All subnets</option>
                    {subnets.map((s) => (
                      <option key={s.id} value={s.id}>
                        {s.subnet}
                      </option>
                    ))}
                  </select>

                  <select
                    value={typeFilter}
                    onChange={(e) => setTypeFilter(e.target.value)}
                    style={{
                      padding: "7px 10px",
                      border: "1px solid #E5E7EB",
                      borderRadius: 7,
                      fontSize: 12,
                      background: "#fff",
                      color: "#374151",
                      cursor: "pointer",
                    }}
                  >
                    <option value="">All types</option>
                    {availableTypes.map((t) => (
                      <option key={t} value={t}>
                        {t.toUpperCase()}
                      </option>
                    ))}
                  </select>

                  <select
                    value={statusFilter}
                    onChange={(e) => setStatusFilter(e.target.value)}
                    style={{
                      padding: "7px 10px",
                      border: "1px solid #E5E7EB",
                      borderRadius: 7,
                      fontSize: 12,
                      background: "#fff",
                      color: "#374151",
                      cursor: "pointer",
                    }}
                  >
                    <option value="">All statuses</option>
                    {availableStatuses.map((s) => (
                      <option key={s} value={s}>
                        {s.charAt(0).toUpperCase() + s.slice(1)}
                      </option>
                    ))}
                  </select>

                  {selectedAssetIds.size > 0 && (() => {
                    const selectedList = assets.filter((a) => selectedAssetIds.has(a.id));
                    const hasOnboarded = selectedList.some((a) => a.status === "onboarded");
                    const hasNew = selectedList.some((a) => a.status !== "onboarded");
                    const buttonText =
                      hasOnboarded && hasNew
                        ? `Deploy / Redeploy Agents (${selectedAssetIds.size})`
                        : hasOnboarded
                        ? `Redeploy Agent (${selectedAssetIds.size})`
                        : `Deploy Agent (${selectedAssetIds.size})`;
                    return (
                      <button
                        onClick={() => handleOpenDeployModal(Array.from(selectedAssetIds))}
                        style={{
                          padding: "6px 14px",
                          background: "#1D4ED8",
                          color: "#fff",
                          border: "none",
                          borderRadius: 6,
                          fontSize: 12.5,
                          fontWeight: 600,
                          cursor: "pointer",
                          display: "flex",
                          alignItems: "center",
                          gap: 6,
                          marginLeft: "auto",
                        }}
                      >
                        <Icon name="send" size={14} />
                        {buttonText}
                      </button>
                    );
                  })()}
                </div>
              )}

              {/* Subnets Actions Row (Subnet Tab) */}
              {!loading && activeTab === "subnets" && subnets.length > 0 && (
                <div
                  style={{
                    padding: "12px 24px",
                    display: "flex",
                    justifyContent: "flex-end",
                  }}
                >
                  <button
                    onClick={() => setShowAddSubnetModal(true)}
                    style={{
                      display: "inline-flex",
                      alignItems: "center",
                      gap: 6,
                      background: "#1D4ED8",
                      color: "#fff",
                      border: "none",
                      borderRadius: 6,
                      padding: "7px 14px",
                      fontSize: 12,
                      fontWeight: 600,
                      cursor: "pointer",
                    }}
                  >
                    <Icon name="plus" size={14} color="#fff" /> Add Subnet
                  </button>
                </div>
              )}
            </div>
          )}

          {/* MAIN SCROLLABLE LIST */}
          <div style={{ flex: 1, overflowY: "auto" }}>
            {loading ? (
              <div
                style={{
                  padding: 24,
                  display: "flex",
                  flexDirection: "column",
                  gap: 10,
                }}
              >
                {[...Array(5)].map((_, i) => (
                  <div
                    key={i}
                    style={{
                      height: 52,
                      background: "#fff",
                      border: "1px solid #E5E7EB",
                      borderRadius: 8,
                      animation: "pulseNew 1.5s infinite ease-in-out",
                    }}
                  />
                ))}
              </div>
            ) : null}

            {/* SUBNETS TABLE VIEW */}
            {!loading && activeTab === "subnets" && (
              <div style={{ padding: "16px 24px" }}>
                {subnets.length === 0 ? (
                  <div
                    style={{
                      background: "#fff",
                      borderRadius: 12,
                      border: "1px solid #E5E7EB",
                      padding: "24px 20px",
                    }}
                  >
                    <EmptyState
                      icon="network"
                      title="No monitored subnets"
                      subtitle="Register CIDR ranges to start scanning and discovering hosts."
                    />
                    <div
                      style={{
                        textAlign: "center",
                        marginTop: -20,
                        marginBottom: 12,
                      }}
                    >
                      <button
                        onClick={() => setShowAddSubnetModal(true)}
                        style={{
                          padding: "7px 16px",
                          background: "#1D4ED8",
                          color: "#fff",
                          border: "none",
                          borderRadius: 7,
                          cursor: "pointer",
                          fontSize: 12.5,
                          fontWeight: 600,
                        }}
                      >
                        Add Subnet
                      </button>
                    </div>
                  </div>
                ) : (
                  <div
                    className="premium-table-container"
                    style={{ marginBottom: 0 }}
                  >
                    <table className="premium-table">
                      <thead>
                        <tr>
                          <th>CIDR Range</th>
                          <th>Description</th>
                          <th>Bootstrap Status</th>
                          <th>Relay Health</th>
                          <th>Last Scan</th>
                          <th style={{ textAlign: "right" }}>Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {getSortedSubnets().map((subnet) => {
                          const hostsCount = assets.filter(
                            (a) => a.subnet_id === subnet.id,
                          ).length;

                          // Bootstrap status
                          let bootstrapText = "Pending";
                          let bootstrapColor = "#6B7280";
                          let bootstrapBg = "#F3F4F6";

                          const bStatus = subnet.bootstrap_status || "pending";
                          if (bStatus === "success" || bStatus === "Active") {
                            bootstrapText = "Configured";
                            bootstrapColor = "#15803D";
                            bootstrapBg = "#F0FDF4";
                          } else if (bStatus === "running") {
                            bootstrapText = "Running…";
                            bootstrapColor = "#1D4ED8";
                            bootstrapBg = "#EFF6FF";
                          } else if (bStatus === "failed") {
                            bootstrapText = "Failed";
                            bootstrapColor = "#DC2626";
                            bootstrapBg = "#FEF2F2";
                          }

                          // Relay health
                          let healthText = "Healthy";
                          let healthColor = "#15803D";
                          let healthBg = "#F0FDF4";

                          if (subnet.status === "Inactive") {
                            healthText = "Inactive";
                            healthColor = "#6B7280";
                            healthBg = "#F3F4F6";
                          } else if (bStatus === "pending") {
                            healthText = "Offline";
                            healthColor = "#DC2626";
                            healthBg = "#FEF2F2";
                          }

                          return (
                            <tr
                              key={subnet.id}
                              onClick={() => setSelectedSubnetDrawer(subnet)}
                              style={{
                                borderBottom: "1px solid #E5E7EB",
                                cursor: "pointer",
                                transition: "background 0.1s",
                              }}
                            >
                              {/* CIDR */}
                              <td
                                style={{
                                  padding: "14px 16px",
                                  fontWeight: 600,
                                  fontSize: 13,
                                  color: "#111827",
                                }}
                              >
                                <div
                                  style={{
                                    display: "flex",
                                    flexDirection: "column",
                                  }}
                                >
                                  <span style={{ fontFamily: "monospace" }}>
                                    {subnet.subnet}
                                  </span>
                                  <span
                                    style={{
                                      fontSize: 11,
                                      color: "#6B7280",
                                      fontWeight: 400,
                                    }}
                                  >
                                    {hostsCount} discovered asset
                                    {hostsCount === 1 ? "" : "s"}
                                  </span>
                                </div>
                              </td>

                              {/* Description */}
                              <td
                                style={{
                                  padding: "14px 16px",
                                  fontSize: 13,
                                  color: "#4B5563",
                                }}
                              >
                                {subnet.name}
                              </td>

                              {/* Bootstrap Status */}
                              <td
                                style={{ padding: "14px 16px", fontSize: 12 }}
                              >
                                <span
                                  style={{
                                    display: "inline-flex",
                                    alignItems: "center",
                                    gap: 5,
                                    padding: "2px 8px",
                                    borderRadius: 12,
                                    background: bootstrapBg,
                                    color: bootstrapColor,
                                    fontWeight: 600,
                                  }}
                                >
                                  {bootstrapText}
                                </span>
                              </td>

                              {/* Relay Health */}
                              <td
                                style={{ padding: "14px 16px", fontSize: 12 }}
                              >
                                <span
                                  style={{
                                    display: "inline-flex",
                                    alignItems: "center",
                                    gap: 5,
                                    padding: "2px 8px",
                                    borderRadius: 12,
                                    background: healthBg,
                                    color: healthColor,
                                    fontWeight: 600,
                                  }}
                                >
                                  <span
                                    style={{
                                      width: 6,
                                      height: 6,
                                      borderRadius: "50%",
                                      background: healthColor,
                                      boxShadow:
                                        healthColor === "#15803D"
                                          ? "0 0 6px #15803D"
                                          : "none",
                                    }}
                                  />
                                  {healthText}
                                </span>
                              </td>

                              {/* Last Scan */}
                              <td
                                style={{
                                  padding: "14px 16px",
                                  fontSize: 12,
                                  color: "#6B7280",
                                  fontFamily: "monospace",
                                }}
                              >
                                {subnet.lastScan}
                              </td>

                              {/* Actions */}
                              <td
                                style={{
                                  padding: "14px 16px",
                                  textAlign: "right",
                                }}
                                onClick={(e) => e.stopPropagation()}
                              >
                                <div
                                  style={{
                                    display: "flex",
                                    gap: 6,
                                    justifyContent: "flex-end",
                                  }}
                                >
                                  <button
                                    onClick={() => {
                                      setSubnetFilter(subnet.id);
                                      setActiveTab("assets");
                                    }}
                                    style={{
                                      padding: "5px 10px",
                                      background: "#fff",
                                      border: "1px solid #D1D5DB",
                                      borderRadius: 6,
                                      fontSize: 12,
                                      cursor: "pointer",
                                      color: "#374151",
                                    }}
                                  >
                                    View Assets
                                  </button>
                                  <button
                                    onClick={() => handleRunSubnetScan(subnet)}
                                    style={{
                                      padding: "5px 10px",
                                      background: "#1D4ED8",
                                      border: "none",
                                      borderRadius: 6,
                                      color: "#fff",
                                      fontSize: 12,
                                      fontWeight: 600,
                                      cursor: "pointer",
                                    }}
                                  >
                                    Auto Discover Assets
                                  </button>
                                  <button
                                    onClick={() => setSubnetToDelete(subnet)}
                                    style={{
                                      padding: "5px 10px",
                                      background: "#FFF",
                                      border: "1px solid #FCA5A5",
                                      borderRadius: 6,
                                      color: "#DC2626",
                                      fontSize: 12,
                                      fontWeight: 600,
                                      cursor: "pointer",
                                      transition: "all 0.15s ease",
                                    }}
                                    onMouseEnter={(e) => {
                                      e.target.style.background = "#FEF2F2";
                                    }}
                                    onMouseLeave={(e) => {
                                      e.target.style.background = "#FFF";
                                    }}
                                  >
                                    Delete
                                  </button>
                                </div>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            )}

            {/* DISCOVERED ASSETS TABLE VIEW */}
            {!loading &&
              activeTab === "assets" &&
              (() => {
                const filteredAssets = getFilteredAssets();
                const totalAssets = filteredAssets.length;
                const assetsPageSize = 10;
                const totalPages = Math.ceil(totalAssets / assetsPageSize) || 1;
                const paginatedAssets = filteredAssets.slice(
                  (assetsPage - 1) * assetsPageSize,
                  assetsPage * assetsPageSize,
                );

                return (
                  <div style={{ padding: "16px 24px" }}>
                    {totalAssets === 0 ? (
                      <div
                        style={{
                          background: "#fff",
                          borderRadius: 12,
                          border: "1px solid #E5E7EB",
                          padding: "24px 20px",
                        }}
                      >
                        {assets.length > 0 ? (
                          <EmptyState
                            icon="search"
                            title="No results found"
                            subtitle="No matching items were found for your search query. Try clearing the search or adjusting filters."
                          />
                        ) : (
                          <EmptyState
                            icon="server"
                            title="No assets discovered"
                            subtitle="Run autodiscovery on a subnet to populate the asset list."
                          />
                        )}
                      </div>
                    ) : (
                      <div
                        className="premium-table-container"
                        style={{ marginBottom: 0 }}
                      >
                        <table className="premium-table">
                          <thead>
                            <tr>
                              <th style={{ width: 40 }}>
                                <input
                                  type="checkbox"
                                  onChange={handleToggleSelectAll}
                                  checked={
                                    selectedAssetIds.size > 0 &&
                                    selectedAssetIds.size ===
                                      filteredAssets.filter(
                                        (a) => (a.management_type || a.managementType) !== "agentless_snmp",
                                      ).length
                                  }
                                />
                              </th>
                              <th>Asset</th>
                              <th>Type</th>
                              <th>IP Address</th>
                              <th>Subnet</th>
                              <th>Open Ports</th>
                              <th>SSH Reachable</th>
                              <th>Last Deploy</th>
                              <th>Last Seen</th>
                              <th style={{ textAlign: "right" }} />
                            </tr>
                          </thead>
                          <tbody>
                            {paginatedAssets.map((asset) => {
                              const cat = getAssetCategory(asset);
                              const mgt = getAssetManagementType(asset);
                              const isStale = asset.status === "stale";
                              const isOnboarded = asset.status === "onboarded";
                              const isChecked = selectedAssetIds.has(asset.id);

                              let typeBg = "#E0E7FF";
                              let typeColor = "#3730A3";
                              if (cat === "database" || cat === "db") {
                                typeBg = "#FEF3C7";
                                typeColor = "#92400E";
                              } else if (
                                ["router", "switch", "firewall", "load_balancer"].includes(cat)
                              ) {
                                typeBg = "#F3E8FF";
                                typeColor = "#6B21A8";
                              }

                              // Status badges
                              let statusText = "Discovered";
                              let statusBg = "#DBEAFE";
                              let statusColor = "#1E40AF";

                              if (isOnboarded) {
                                statusText = mgt === "agentless_snmp" ? "Agentless Monitored" : "Onboarded";
                                statusBg = "#D1FAE5";
                                statusColor = "#065F46";
                              } else if (isStale) {
                                statusText = "Stale";
                                statusBg = "#FEF3C7";
                                statusColor = "#92400E";
                              }

                              return (
                                <tr
                                  key={asset.id}
                                  onClick={() => setSelectedAssetDrawer(asset)}
                                  style={{
                                    borderBottom: "1px solid #E5E7EB",
                                    cursor: "pointer",
                                    opacity: isStale ? 0.75 : 1,
                                  }}
                                >
                                  {/* Selection Checkbox */}
                                  <td
                                    style={{ padding: "14px 16px" }}
                                    onClick={(e) => e.stopPropagation()}
                                  >
                                    <div
                                      style={{
                                        display: "flex",
                                        alignItems: "center",
                                        gap: 6,
                                      }}
                                    >
                                      <input
                                        type="checkbox"
                                        checked={isChecked}
                                        disabled={mgt === "agentless_snmp"}
                                        onChange={() =>
                                          handleToggleSelectAsset(asset.id)
                                        }
                                      />
                                      {isStale && (
                                        <Tooltip text="Stale host warning: Potential DHCP IP reassignment. Agent push might trigger on the wrong server!">
                                          <span>
                                            <Icon
                                              name="alert"
                                              size={14}
                                              color="#D97706"
                                            />
                                          </span>
                                        </Tooltip>
                                      )}
                                    </div>
                                  </td>

                                  {/* Asset name */}
                                  <td
                                    style={{
                                      padding: "14px 16px",
                                      fontWeight: 600,
                                      fontSize: 13,
                                      color: "#111827",
                                    }}
                                  >
                                    {asset.name || "unnamed"}
                                  </td>

                                  {/* Type Chip */}
                                  <td
                                    style={{
                                      padding: "14px 16px",
                                      fontSize: 12,
                                    }}
                                  >
                                    <span
                                      style={{
                                        padding: "2px 8px",
                                        borderRadius: 4,
                                        background: typeBg,
                                        color: typeColor,
                                        fontFamily: "monospace",
                                        fontWeight: 600,
                                        fontSize: 10.5,
                                        textTransform: "uppercase",
                                      }}
                                    >
                                      {asset.type}
                                    </span>
                                  </td>

                                  {/* IP */}
                                  <td
                                    style={{
                                      padding: "14px 16px",
                                      fontSize: 13,
                                      color: "#374151",
                                      fontFamily: "monospace",
                                    }}
                                  >
                                    {asset.ip}
                                  </td>

                                  {/* Subnet */}
                                  <td
                                    style={{
                                      padding: "14px 16px",
                                      fontSize: 13,
                                      color: "#374151",
                                    }}
                                  >
                                    {(() => {
                                      const subnetObj = subnets.find(
                                        (s) => s.id === asset.subnet_id,
                                      );
                                      return subnetObj
                                        ? `${subnetObj.name} (${subnetObj.subnet})`
                                        : asset.subnet_id;
                                    })()}
                                  </td>

                                  {/* Ports */}
                                  <td style={{ padding: "14px 16px" }}>
                                    <div
                                      style={{
                                        display: "flex",
                                        gap: 4,
                                        flexWrap: "wrap",
                                      }}
                                    >
                                      {asset.openPorts?.map((p) => (
                                        <span
                                          key={p}
                                          style={{
                                            fontSize: 10.5,
                                            fontFamily: "monospace",
                                            background: "#F1F5F9",
                                            color: "#475569",
                                            padding: "1px 5px",
                                            borderRadius: 4,
                                            border: "1px solid #E2E8F0",
                                          }}
                                        >
                                          {p}
                                        </span>
                                      ))}
                                    </div>
                                  </td>

                                  {/* SSH Reachable */}
                                  <td
                                    style={{
                                      padding: "14px 16px",
                                      fontSize: 12,
                                    }}
                                  >
                                    {asset.sshReachable === "Unreachable" ? (
                                      <span
                                        style={{
                                          color: "#DC2626",
                                          fontWeight: 600,
                                        }}
                                      >
                                        Unreachable
                                      </span>
                                    ) : asset.sshReachable === "Success" ? (
                                      <span
                                        style={{
                                          color: "#15803D",
                                          fontWeight: 600,
                                        }}
                                      >
                                        Success
                                      </span>
                                    ) : (
                                      <span style={{ color: "#9CA3AF" }}>
                                        —
                                      </span>
                                    )}
                                  </td>

                                  {/* Last Deploy */}
                                  <td
                                    style={{
                                      padding: "14px 16px",
                                      fontSize: 12,
                                    }}
                                  >
                                    {asset.lastDeployStatus ? (
                                      <div
                                        style={{
                                          display: "flex",
                                          flexDirection: "column",
                                        }}
                                      >
                                        <span
                                          style={{
                                            fontWeight: 600,
                                            color:
                                              asset.lastDeployStatus.status ===
                                              "success"
                                                ? "#15803D"
                                                : "#DC2626",
                                            textTransform: "capitalize",
                                          }}
                                        >
                                          {asset.lastDeployStatus.status}
                                        </span>
                                        <span
                                          style={{
                                            fontSize: 9.5,
                                            color: "#9CA3AF",
                                          }}
                                        >
                                          {asset.lastDeployStatus.timestamp}
                                        </span>
                                      </div>
                                    ) : (
                                      <span style={{ color: "#9CA3AF" }}>
                                        —
                                      </span>
                                    )}
                                  </td>

                                  {/* Last Seen */}
                                  <td
                                    style={{
                                      padding: "14px 16px",
                                      fontSize: 12,
                                    }}
                                  >
                                    <span
                                      style={{
                                        display: "inline-flex",
                                        alignItems: "center",
                                        gap: 5,
                                        padding: "2px 8px",
                                        borderRadius: 12,
                                        background: statusBg,
                                        color: statusColor,
                                        fontWeight: 600,
                                      }}
                                    >
                                      {statusText}
                                    </span>
                                  </td>

                                  {/* Actions */}
                                  <td
                                    style={{
                                      padding: "14px 16px",
                                      textAlign: "right",
                                    }}
                                    onClick={(e) => e.stopPropagation()}
                                  >
                                    <div
                                      style={{ display: "inline-flex", gap: 6 }}
                                    >
                                      {/* Test connection */}
                                      <Tooltip
                                        text={
                                          connectionTestResult[asset.ip]
                                            ? connectionTestResult[asset.ip]
                                                .details
                                            : "Test SSH/Sudo credentials"
                                        }
                                      >
                                        <button
                                          onClick={(e) =>
                                            handleTestConnection(asset, e)
                                          }
                                          disabled={
                                            testingConnectionIp === asset.ip ||
                                            mgt === "agentless_snmp"
                                          }
                                          style={{
                                            padding: "4px 8px",
                                            background: "#fff",
                                            border: "1px solid #D1D5DB",
                                            borderRadius: 6,
                                            cursor: isOnboarded
                                              ? "not-allowed"
                                              : "pointer",
                                            display: "flex",
                                            alignItems: "center",
                                            justifyContent: "center",
                                          }}
                                        >
                                          {testingConnectionIp === asset.ip ? (
                                            <div
                                              style={{
                                                width: 12,
                                                height: 12,
                                                borderRadius: "50%",
                                                border:
                                                  "2px solid rgba(0,0,0,0.1)",
                                                borderTopColor: "#1D4ED8",
                                                animation:
                                                  "spin 0.6s linear infinite",
                                              }}
                                            />
                                          ) : connectionTestResult[asset.ip] ? (
                                            connectionTestResult[asset.ip]
                                              .success ? (
                                              <Icon
                                                name="check"
                                                size={12}
                                                color="#15803D"
                                              />
                                            ) : (
                                              <Icon
                                                name="alert"
                                                size={12}
                                                color="#DC2626"
                                              />
                                            )
                                          ) : (
                                            <Icon
                                              name="activity"
                                              size={12}
                                              color="#6B7280"
                                            />
                                          )}
                                        </button>
                                      </Tooltip>

                                      {mgt === "agentless_snmp" ? (
                                        <span className="badge badge-status-active">
                                          Agentless Monitored
                                        </span>
                                      ) : isOnboarded ? (
                                        <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                                          <span className="badge badge-status-active">
                                            Onboarded
                                          </span>
                                          <button
                                            onClick={(e) => {
                                              e.stopPropagation();
                                              handleOpenDeployModal([asset.id], { isRedeploy: true });
                                            }}
                                            title="Redeploy agent to apply fixes or restart services"
                                            style={{
                                              padding: "4px 8px",
                                              background: "#F3F4F6",
                                              border: "1px solid #D1D5DB",
                                              borderRadius: 6,
                                              color: "#374151",
                                              fontSize: 11.5,
                                              fontWeight: 600,
                                              cursor: "pointer",
                                              display: "flex",
                                              alignItems: "center",
                                              gap: 4,
                                              transition: "all 0.15s ease",
                                            }}
                                            onMouseEnter={(e) => {
                                              e.currentTarget.style.background = "#EFF6FF";
                                              e.currentTarget.style.borderColor = "#93C5FD";
                                              e.currentTarget.style.color = "#1D4ED8";
                                            }}
                                            onMouseLeave={(e) => {
                                              e.currentTarget.style.background = "#F3F4F6";
                                              e.currentTarget.style.borderColor = "#D1D5DB";
                                              e.currentTarget.style.color = "#374151";
                                            }}
                                          >
                                            <Icon name="refresh-cw" size={11} />
                                            Redeploy
                                          </button>
                                        </div>
                                      ) : (
                                        <button
                                          onClick={(e) => {
                                            e.stopPropagation();
                                            handleOpenDeployModal([asset.id], { isRedeploy: false });
                                          }}
                                          style={{
                                            padding: "5px 10px",
                                            background: "#1D4ED8",
                                            border: "none",
                                            borderRadius: 6,
                                            color: "#fff",
                                            fontSize: 12,
                                            fontWeight: 600,
                                            cursor: "pointer",
                                          }}
                                        >
                                          Deploy Agent
                                        </button>
                                      )}
                                    </div>
                                  </td>
                                </tr>
                              );
                            })}
                          </tbody>
                        </table>

                        {/* Pagination Controls */}
                        <div
                          style={{
                            display: "flex",
                            justifyContent: "space-between",
                            alignItems: "center",
                            padding: "12px 24px",
                            borderTop: "1px solid #E5E7EB",
                            background: "#F9FAFB",
                            borderBottomLeftRadius: 12,
                            borderBottomRightRadius: 12,
                          }}
                        >
                          <div style={{ fontSize: 13, color: "#6B7280" }}>
                            Showing{" "}
                            <strong>
                              {Math.min(
                                totalAssets,
                                (assetsPage - 1) * assetsPageSize + 1,
                              )}
                            </strong>{" "}
                            to{" "}
                            <strong>
                              {Math.min(
                                totalAssets,
                                assetsPage * assetsPageSize,
                              )}
                            </strong>{" "}
                            of <strong>{totalAssets}</strong> assets
                          </div>
                          <div style={{ display: "flex", gap: 6 }}>
                            <button
                              disabled={assetsPage === 1}
                              onClick={() =>
                                setAssetsPage((prev) => Math.max(1, prev - 1))
                              }
                              style={{
                                padding: "6px 12px",
                                background: "#fff",
                                border: "1px solid #D1D5DB",
                                borderRadius: 6,
                                fontSize: 12,
                                fontWeight: 500,
                                color: assetsPage === 1 ? "#9CA3AF" : "#374151",
                                cursor:
                                  assetsPage === 1 ? "not-allowed" : "pointer",
                              }}
                            >
                              Previous
                            </button>
                            <button
                              disabled={assetsPage === totalPages}
                              onClick={() =>
                                setAssetsPage((prev) =>
                                  Math.min(totalPages, prev + 1),
                                )
                              }
                              style={{
                                padding: "6px 12px",
                                background: "#fff",
                                border: "1px solid #D1D5DB",
                                borderRadius: 6,
                                fontSize: 12,
                                fontWeight: 500,
                                color:
                                  assetsPage === totalPages
                                    ? "#9CA3AF"
                                    : "#374151",
                                cursor:
                                  assetsPage === totalPages
                                    ? "not-allowed"
                                    : "pointer",
                              }}
                            >
                              Next
                            </button>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                );
              })()}
          </div>
        </div>
      )}

      {/* ================= ADD SUBNET MODAL ================= */}
      <SubnetRelayWizardModal
        isOpen={showAddSubnetModal}
        onClose={() => setShowAddSubnetModal(false)}
        onSuccess={loadData}
      />

      {/* ================= DELETE CONFIRMATION MODAL ================= */}
      <ConfirmModal
        isOpen={Boolean(subnetToDelete)}
        title="Delete Subnet"
        onClose={() => {
          setSubnetToDelete(null);
          setDeleteSubnetError(null);
        }}
        onConfirm={handleConfirmDeleteSubnet}
        confirmText="Delete Subnet"
        variant="danger"
      >
        {subnetToDelete && (
          <div>
            <div style={{ fontSize: 13, color: "#4B5563", lineHeight: 1.4 }}>
              Are you sure you want to delete subnet{" "}
              <strong>{subnetToDelete.subnet}</strong>? This will also stop
              monitoring for this network range.
            </div>
            {deleteSubnetError && (
              <div
                style={{
                  color: "#DC2626",
                  fontSize: 12.5,
                  fontWeight: 500,
                  marginTop: 6,
                }}
              >
                ❌ {deleteSubnetError}
              </div>
            )}
          </div>
        )}
      </ConfirmModal>

      {/* ================= DEPLOY CONFIRM MODAL ================= */}
      <DeployToMonitoringModal
        isOpen={showDeployModal}
        onClose={() => setShowDeployModal(false)}
        assets={assets}
        deployFields={deployFields}
        setDeployFields={setDeployFields}
        deployConcurrency={deployConcurrency}
        setDeployConcurrency={setDeployConcurrency}
        authorizedBy={getAuthorizedByName()}
        onStartDeploy={handleStartDeploy}
        isRedeploy={isRedeployMode}
      />

      {/* ================= SUBNET DISCOVERY SCAN OVERLAY MODAL ================= */}
      <SubnetScanOverlayModal
        activeScanSubnet={activeScanSubnet}
        scanProgress={scanProgress}
        scanTerminalLines={scanTerminalLines}
        scanTerminalBottomRef={scanTerminalBottomRef}
        scanResultSummary={scanResultSummary}
        scanFinished={scanFinished}
        onClose={() => setActiveScanSubnet(null)}
        onDone={() => {
          setActiveScanSubnet(null);
          setActiveTab("assets");
        }}
      />

      {/* ================= SLIDE-OVER DETAIL DRAWER: SUBNETS ================= */}
      <SubnetProfileDrawer
        subnet={selectedSubnetDrawer}
        onClose={() => setSelectedSubnetDrawer(null)}
        assets={assets}
        subnets={subnets}
      />

      {/* ================= SLIDE-OVER DETAIL DRAWER: ASSETS ================= */}
      <AssetProfileDrawer
        asset={selectedAssetDrawer}
        onClose={() => setSelectedAssetDrawer(null)}
        subnets={subnets}
        connectionTestResult={connectionTestResult}
        onDeployAgent={handleOpenDeployModal}
      />


      {operationError && (
        <Modal
          title="Error"
          onClose={() => setOperationError(null)}
          width={400}
        >
          <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
            <div style={{ display: "flex", gap: 12, alignItems: "center" }}>
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  width: 36,
                  height: 36,
                  borderRadius: "50%",
                  background: "#FEF2F2",
                  border: "1px solid #FCA5A5",
                  flexShrink: 0,
                }}
              >
                <Icon name="alert" size={20} color="#DC2626" />
              </div>
              <div style={{ fontSize: 13, color: "#374151", fontWeight: 500 }}>
                {operationError}
              </div>
            </div>
            <div
              style={{
                display: "flex",
                justifyContent: "flex-end",
                borderTop: "1px solid #F3F4F6",
                paddingTop: 12,
                marginTop: 4,
              }}
            >
              <button
                onClick={() => setOperationError(null)}
                style={{
                  padding: "6px 14px",
                  background: "#EF4444",
                  color: "#fff",
                  border: "none",
                  borderRadius: 6,
                  fontSize: 12,
                  fontWeight: 600,
                  cursor: "pointer",
                }}
              >
                Dismiss
              </button>
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
}
